<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import _ from 'lodash';
import moment from 'moment';
import Spinner from "@/components/ui/Spinner.vue";
import PaywallService from '@/services/PaywallService';
import { useNotification } from "@kyvg/vue3-notification"
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconSearch from '@/components/icon/IconSearch.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconUsers from '@/components/icon/IconUsers.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';
import { useStore } from 'vuex';

const props = defineProps({
	paywallStreamId: {
		type: Number,
		require: false
	}
})

const { notify } = useNotification()
const store = useStore()

// Refs
const users = ref([])
const loading = ref(false)
const search = ref('')

// Computed
const currentPaywall = computed(() => store.getters['Paywalls/currentPaywall'])

const scopedUsers = computed(() => {
	if (!props.paywallStreamId) return users.value
	return _.filter(users.value, { stream_id: props.paywallStreamId })
})

const filteredUsers = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return scopedUsers.value
	return scopedUsers.value.filter((u) =>
		String(u.name || '').toLowerCase().includes(query) ||
		String(u.customer || '').toLowerCase().includes(query)
	)
})

const hasUsers = computed(() => scopedUsers.value.length > 0)

// Client-side pagination (full set is already loaded).
const PAGE_SIZE = 25
const page = ref(1)
const totalPages = computed(() => Math.max(1, Math.ceil(filteredUsers.value.length / PAGE_SIZE)))
const pagedUsers = computed(() => {
	const start = (page.value - 1) * PAGE_SIZE
	return filteredUsers.value.slice(start, start + PAGE_SIZE)
})
const rangeStart = computed(() => (filteredUsers.value.length ? (page.value - 1) * PAGE_SIZE + 1 : 0))
const rangeEnd = computed(() => Math.min(page.value * PAGE_SIZE, filteredUsers.value.length))
watch(() => [search.value, scopedUsers.value.length], () => { page.value = 1 })
watch(totalPages, (tp) => { if (page.value > tp) page.value = tp })

// Subtle zebra striping for easier row scanning
const zebraBg = (idx) => idx % 2 === 0 ? 'bg-surface-2/60' : 'bg-surface-2/25'

const clearSearch = () => { search.value = '' }

onMounted(async () => {
	await setupUsers();
})

const setupUsers = async () => {
	loading.value = true;
	try {
		const usersData = await PaywallService.getUsers();
		let flattenInvoices = _.reduce(usersData, (result, value) => {
			const user = _.pick(value, ['id', 'name', 'email', 'email_verified_at', 'created_at']);
			const invoices = _.map(value.invoices, function (el) {
				return _.extend({}, el, user);
			});
			return [...result, ...invoices]
		}, []);

		// each user could be subscribed to stream N times, get latest subscription to stream from list
		let streamInvoices = _.chain(flattenInvoices).groupBy('stream_id').reduce((result, value) => {
			const invoices = _.uniqBy(_.sortBy(value, (item) => item.started_at).reverse(), 'id');
			return [...result, ...invoices]
		}, []).value();

		users.value = _.map(streamInvoices, (user) => {
			return {
				id: user.id,
				name: user.name,
				customer: user.email || 'n/a',
				stream_id: user.stream_id,
				subscription_start: moment(new Date(user.started_at)).format('MMM DD, YYYY · hh:mm A'),
			}
		}).reverse();
	} catch (err) {
		notify({ group: 'error', text: err.message || "Couldn't load customers" });
		console.error('getUsers failed', err);
	}
	loading.value = false;
}

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

const onBtnExport = () => {
	if (!filteredUsers.value.length) return
	const header = ['Name', 'Email', 'Subscribed']
	const rows = filteredUsers.value.map((u) => [u.name, u.customer, u.subscription_start])
	const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n')
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = `${currentPaywall.value?.title || 'paywall'}-customers.csv`
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}
</script>

<template>
	<section class="bg-surface-1 rounded-xl p-4 sm:p-5">
		<!-- Header -->
		<div class="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
			<div class="min-w-0 grow">
				<h3 class="text-sm font-semibold text-white mb-0.5">Customers</h3>
				<p class="text-tiny text-surface-7 mb-0">Everyone who has purchased access to this paywall.</p>
			</div>
			<FormButton type="secondary" size="sm" class="shrink-0" :disabled="!filteredUsers.length" @click="onBtnExport">
				<icon-base fill="none" class="!mr-1.5">
					<icon-download />
				</icon-base>
				Export CSV
			</FormButton>
		</div>

		<!-- Search -->
		<div v-if="hasUsers" class="flex items-center mb-3 relative max-w-xs">
			<icon-base class="absolute z-10 left-2 text-surface-7">
				<icon-search />
			</icon-base>
			<FormInput placeholder="Search by name or email" customClasses="!px-8" v-model="search" />
			<FormButton class="absolute z-10 right-0" isIcon type="link" v-if="search" @click="clearSearch">
				<icon-base>
					<icon-cross />
				</icon-base>
			</FormButton>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="flex items-center justify-center min-h-[160px]">
			<Spinner spinner-color="var(--c-dark-8)" />
		</div>

		<!-- Empty: no customers at all -->
		<div v-else-if="!hasUsers"
			class="flex flex-col items-center justify-center text-center gap-2 rounded-lg border border-dashed border-surface-3 px-6 py-10">
			<div class="flex size-11 items-center justify-center rounded-full bg-surface-3 text-surface-6">
			<icon-base :width="22" :height="22" :width-view-box="24" :height-view-box="24" fill="none">
				<icon-users />
			</icon-base>
			</div>
			<p class="text-sm font-medium text-surface-8 mb-0">No customers yet</p>
			<p class="text-tiny text-surface-6 mb-0 max-w-sm">Viewers who purchase access to this paywall will appear
				here.</p>
		</div>

		<!-- Table -->
		<template v-else>
			<div class="overflow-x-auto">
				<table class="w-full min-w-[640px] border-separate border-spacing-y-1 text-left">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="font-semibold px-4 pb-1 w-12">#</th>
							<th class="font-semibold px-4 pb-1">Name</th>
							<th class="font-semibold px-4 pb-1">Email</th>
							<th class="font-semibold px-4 pb-1 w-56">Subscribed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!filteredUsers.length">
							<td colspan="4" class="px-4 py-8 text-center text-tiny text-surface-6">
								No results for “{{ search }}”.
							</td>
						</tr>
						<tr v-for="(user, idx) in pagedUsers" :key="`${user.id}-${user.stream_id}`" class="group">
							<!-- # -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-l-lg"
								:class="zebraBg(idx)">
								<span class="text-tiny text-surface-6 tabular-nums">{{ rangeStart + idx }}</span>
							</td>
							<!-- Name -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<span class="text-sm text-surface-text-1 truncate" :title="user.name">{{ user.name || '—' }}</span>
							</td>
							<!-- Email -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<div class="flex items-center gap-1.5 min-w-0">
									<span class="text-sm text-surface-8 truncate" :title="user.customer">{{ user.customer }}</span>
									<ButtonCopyToClipboard
										class="shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
										:text="user.customer" />
								</div>
							</td>
							<!-- Subscribed -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-r-lg"
								:class="zebraBg(idx)">
								<span class="text-sm text-surface-8 text-nowrap">{{ user.subscription_start }}</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="flex flex-wrap items-center justify-between gap-3 mt-3">
				<p class="text-tiny text-surface-6 mb-0">
					<template v-if="filteredUsers.length">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ filteredUsers.length }}
						{{ filteredUsers.length === 1 ? 'customer' : 'customers' }}</template>
				</p>
				<div v-if="totalPages > 1" class="flex items-center gap-2">
					<FormButton type="secondary" size="sm" :disabled="page <= 1" @click="page--">Prev</FormButton>
					<span class="text-tiny text-surface-7 tabular-nums">Page {{ page }} of {{ totalPages }}</span>
					<FormButton type="secondary" size="sm" :disabled="page >= totalPages" @click="page++">Next</FormButton>
				</div>
			</div>
		</template>
	</section>
</template>

<style scoped></style>
